'use client';

import { useState, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { ChevronDown, Mail, Search, MessageCircle, HelpCircle, ArrowRight, Clock, Headphones } from 'lucide-react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { PAGE_CONTAINER } from '@/components/ui';
import type { FaqCategoryWithFaqs, FaqItem } from '@/lib/api';

interface FlatFaq extends FaqItem {
  categoryId: string;
  categoryName: string;
}

interface HelpPageClientProps {
  faqCategories: FaqCategoryWithFaqs[];
}

export function HelpPageClient({ faqCategories }: HelpPageClientProps) {
  const t = useTranslations('help');

  const allFlatFaqs: FlatFaq[] = faqCategories.flatMap((cat) =>
    cat.faqs.map((faq) => ({ ...faq, categoryId: cat.id, categoryName: cat.name })),
  );

  const categoryFilters = [
    { id: 'all', label: t('semua') },
    ...faqCategories.filter((c) => c.id).map((c) => ({ id: c.id, label: c.name })),
  ];

  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(allFlatFaqs[0]?.id ?? null);

  const filteredFaqs = useMemo(() => {
    let result = allFlatFaqs;
    if (activeCategory !== 'all') {
      result = result.filter((faq) => faq.categoryId === activeCategory);
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (faq) =>
          faq.question.toLowerCase().includes(query) ||
          faq.answer.toLowerCase().includes(query),
      );
    }
    return result;
  }, [activeCategory, searchQuery, allFlatFaqs]);

  return (
    <>
      {/* Header with Search */}
      <div className="bg-pattern bg-teal-700 py-8 lg:py-12">
        <div className={PAGE_CONTAINER}>
          <div className="text-center mb-5 lg:mb-8">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-white mb-2">{t('title')}</h1>
            <p className="text-teal-50 text-sm">{t('subtitle')}</p>
          </div>

          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 lg:py-4 rounded-2xl bg-white border-0 text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-teal-500 focus:outline-none text-sm"
            />
          </div>

          {/* Category chips — mobile */}
          <div className="flex overflow-x-auto gap-2 pt-4 -mx-4 px-4 scrollbar-hide lg:hidden">
            {categoryFilters.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={cn(
                  'flex-shrink-0 whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                  activeCategory === cat.id
                    ? 'bg-white text-teal-800'
                    : 'bg-white/10 text-teal-50 hover:bg-white/20',
                )}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="bg-slate-50 py-4 lg:py-8">
        <div className={PAGE_CONTAINER}>
          {allFlatFaqs.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <HelpCircle className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">{t('noFaqs')}</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Left Sidebar — desktop */}
              <aside className="hidden lg:block lg:col-span-1">
                <div className="lg:sticky lg:top-24 space-y-4">
                  <div className="bg-white rounded-2xl border border-slate-200 p-4">
                    <h4 className="font-semibold text-slate-900 text-sm mb-3">{t('kategori')}</h4>
                    <div className="flex flex-wrap gap-2">
                      {categoryFilters.map((cat) => (
                        <button
                          key={cat.id}
                          onClick={() => setActiveCategory(cat.id)}
                          className={cn(
                            'whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                            activeCategory === cat.id
                              ? 'bg-teal-600 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
                          )}
                        >
                          {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl border border-slate-200 p-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-3 bg-slate-50 rounded-xl">
                        <Headphones className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                        <div className="text-lg font-bold text-slate-900">{t('supportHoursValue')}</div>
                        <div className="text-sm text-slate-500">{t('supportHours')}</div>
                      </div>
                      <div className="text-center p-3 bg-slate-50 rounded-xl">
                        <Clock className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                        <div className="text-lg font-bold text-slate-900">{t('responseTimeValue')}</div>
                        <div className="text-sm text-slate-500">{t('responseTime')}</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-teal-700 rounded-2xl p-5 text-white">
                    <div className="flex items-center gap-2 mb-3">
                      <MessageCircle className="w-5 h-5" />
                      <h4 className="font-semibold text-sm">{t('masihPunyaPertanyaan')}</h4>
                    </div>
                    <p className="text-sm text-teal-50 mb-4">{t('teamReady')}</p>
                    <div className="space-y-2">
                      <a
                        href="mailto:support@aksademia.com"
                        className="flex items-center gap-2 text-sm text-white hover:text-teal-100"
                      >
                        <Mail className="w-4 h-4" />
                        support@aksademia.com
                      </a>
                      <Link
                        href="#"
                        className="flex items-center gap-2 text-xs font-semibold bg-white/20 hover:bg-white/30 px-3 py-2 rounded-lg transition-colors"
                      >
                        {t('chatDenganKami')}
                        <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </div>
              </aside>

              {/* Main Content */}
              <div className="lg:col-span-2">
                {filteredFaqs.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
                    <HelpCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <h3 className="text-base font-semibold text-slate-900 mb-1">{t('noResults')}</h3>
                    <p className="text-slate-500 text-sm">{t('noResultsDesc')}</p>
                    <button
                      onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}
                      className="mt-3 text-teal-600 font-medium text-sm hover:underline"
                    >
                      {t('resetSearch')}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredFaqs.map((faq) => (
                      <div
                        key={faq.id}
                        className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-teal-200 transition-colors"
                      >
                        <button
                          onClick={() => setOpenId(openId === faq.id ? null : faq.id)}
                          className="w-full flex items-center justify-between p-3 lg:p-4 text-left hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-center gap-2.5">
                            <div className={cn(
                              'w-7 h-7 rounded-lg flex items-center justify-center transition-colors flex-shrink-0',
                              openId === faq.id ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-500',
                            )}>
                              <HelpCircle className="w-3.5 h-3.5" />
                            </div>
                            <div className="text-left min-w-0">
                              {faq.categoryName && (
                                <span className="text-xs text-teal-700 font-medium uppercase tracking-wide">
                                  {faq.categoryName}
                                </span>
                              )}
                              <p className="font-semibold text-slate-900 text-sm leading-snug">{faq.question}</p>
                            </div>
                          </div>
                          <ChevronDown className={cn(
                            'w-4 h-4 text-slate-400 transition-transform duration-300 flex-shrink-0 ml-2',
                            openId === faq.id ? 'rotate-180' : '',
                          )} />
                        </button>

                        <div className={cn(
                          'overflow-hidden transition-all duration-300',
                          openId === faq.id ? 'max-h-56' : 'max-h-0',
                        )}>
                          <div className="px-3 lg:px-4 pb-3 lg:pb-4">
                            <p className="max-w-3xl text-sm text-slate-600 leading-relaxed lg:pl-[38px]">{faq.answer}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="hidden lg:block mt-6 bg-white rounded-2xl border border-slate-200 p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h4 className="font-semibold text-slate-900 text-sm">{t('stillHaveQuestion')}</h4>
                      <p className="text-sm text-slate-500">{t('stillHaveQuestionDesc')}</p>
                    </div>
                    <a
                      href="mailto:support@aksademia.com"
                      className="inline-flex items-center gap-1.5 text-teal-600 font-semibold text-sm hover:text-teal-700 transition-colors"
                    >
                      {t('hubungiSupport')}
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Floating contact button — mobile */}
      <a
        href="mailto:support@aksademia.com"
        className="fixed bottom-5 right-4 z-50 lg:hidden bg-teal-700 text-white rounded-full px-4 py-3 flex items-center gap-2 shadow-lg text-sm font-medium"
      >
        <MessageCircle className="w-4 h-4" />
        {t('hubungiKami')}
      </a>
    </>
  );
}
